/**
 * Live activity placement (ticket 3525, upstream pin e3e7cc3f, MIT), ported
 * from upstream `MessagesTimeline.logic.ts`: derives the shared live work row
 * (or the thinking placeholder) for the turn in flight, so tools, thoughts,
 * and spawn batches read as one activity row while they run.
 *
 * @module MessagesTimeline.liveActivity
 */

import type { TurnId } from "@cmmd-forge/contracts";
import {
  workEntryDisplayIndicatesToolFailure,
  workEntryIndicatesToolSuccess,
  type TimelineEntry,
} from "../../session-logic";
import { omitSupersededLifecycleMarkers } from "../../workLogToolGroups";
import {
  workEntryIsActiveTurnActivity,
  workEntryIsVisibleInGroup,
  workGroupId,
  workGroupIdentity,
} from "./MessagesTimeline.activityGroups";
import { LIVE_ACTIVITY_ROW_ID, type MessagesTimelineRow } from "./MessagesTimeline.rowTypes";

export interface ActiveWorkState {
  readonly activeTurnHeaderIndex: number;
  readonly activeWorkRow: Extract<MessagesTimelineRow, { kind: "work-live" }> | null;
  readonly activeWorkEntryIds: ReadonlySet<string>;
  readonly latestToolFailed: boolean;
  readonly latestToolKeepsActivityLive: boolean;
  /** Whether any (possibly filtered-out) live tool candidate exists at all. */
  readonly hasLatestVisibleToolEntry: boolean;
  readonly activeWorkAnchorId: string | null;
}

// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.logic.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function deriveActiveWorkState(input: {
  timelineEntries: ReadonlyArray<TimelineEntry>;
  isWorking: boolean;
  unsettledTurnId: TurnId | null;
  expandedWorkGroupIds?: ReadonlySet<string> | undefined;
  /** Task ids of subagents still working, used by the active tool indicator. */
  liveAgentTaskIds?: ReadonlySet<string> | undefined;
  lastUserMessageIndex: number;
}): ActiveWorkState {
  const { timelineEntries, isWorking, unsettledTurnId } = input;
  const activeTurnHeaderIndex = isWorking ? input.lastUserMessageIndex + 1 : timelineEntries.length;
  const empty: ActiveWorkState = {
    activeTurnHeaderIndex,
    activeWorkRow: null,
    activeWorkEntryIds: new Set(),
    latestToolFailed: false,
    latestToolKeepsActivityLive: false,
    hasLatestVisibleToolEntry: false,
    activeWorkAnchorId: null,
  };
  if (!isWorking) {
    return empty;
  }

  const entryBelongsToActiveTurn = (entry: TimelineEntry, index: number) =>
    index >= activeTurnHeaderIndex &&
    (unsettledTurnId === null || timelineEntryTurnIdOf(entry) === unsettledTurnId);
  const activeToolEntries: Array<Extract<TimelineEntry, { kind: "work" }>> = [];
  for (let index = timelineEntries.length - 1; index >= activeTurnHeaderIndex; index -= 1) {
    const entry = timelineEntries[index]!;
    if (
      !entryBelongsToActiveTurn(entry, index) ||
      entry.kind !== "work" ||
      entry.entry.questionAnswer !== undefined ||
      entry.entry.githubConfirmation !== undefined ||
      entry.entry.githubActionRequired !== undefined ||
      entry.entry.sourceActivityKind === "context-compaction" ||
      entry.entry.tone === "error"
    ) {
      break;
    }
    activeToolEntries.unshift(entry);
    if (workEntryDisplayIndicatesToolFailure(entry.entry)) break;
  }
  if (activeToolEntries.length === 0) {
    return { ...empty, activeTurnHeaderIndex };
  }
  const visibleActiveToolEntries = omitSupersededLifecycleMarkers(
    activeToolEntries.filter((entry) => workEntryIsVisibleInGroup(entry.entry, true)),
    (entry) => entry.entry,
  );
  const activeWorkAnchor = activeToolEntries[0]!;
  const latestVisibleToolEntry = visibleActiveToolEntries.at(-1);
  const latestRunningToolEntry = visibleActiveToolEntries.findLast((entry) => {
    const spawn = entry.entry.agentSpawn;
    return spawn
      ? entry === latestVisibleToolEntry &&
          ((spawn.workflowId !== null && input.liveAgentTaskIds?.has(spawn.workflowId)) ||
            spawn.agentTaskIds.some((taskId) => input.liveAgentTaskIds?.has(taskId)))
      : workEntryIsActiveTurnActivity(entry.entry);
  });
  const latestToolFailed =
    latestRunningToolEntry === undefined &&
    latestVisibleToolEntry !== undefined &&
    latestVisibleToolEntry.entry.toolLifecycleStatus !== "declined" &&
    workEntryDisplayIndicatesToolFailure(latestVisibleToolEntry.entry);
  const latestToolKeepsActivityLive =
    latestRunningToolEntry !== undefined ||
    (latestVisibleToolEntry !== undefined &&
      latestVisibleToolEntry.entry.agentSpawn === undefined &&
      (workEntryIndicatesToolSuccess(latestVisibleToolEntry.entry) ||
        (latestVisibleToolEntry.entry.toolLifecycleStatus === "completed" &&
          !workEntryDisplayIndicatesToolFailure(latestVisibleToolEntry.entry))));
  const activeWorkPlacementEntryId = latestVisibleToolEntry?.id;
  const activeWorkRow =
    activeWorkAnchor && latestVisibleToolEntry && !latestToolFailed
      ? (() => {
          const groupId = workGroupId(activeWorkAnchor.id, activeWorkAnchor.entry);
          return {
            kind: "work-live" as const,
            id: latestToolKeepsActivityLive
              ? LIVE_ACTIVITY_ROW_ID
              : `work-live:${workGroupIdentity(activeWorkAnchor.id, activeWorkAnchor.entry)}`,
            createdAt: activeWorkAnchor.createdAt,
            entry: (latestRunningToolEntry ?? latestVisibleToolEntry).entry,
            groupedEntries: visibleActiveToolEntries.map((entry) => entry.entry),
            groupId,
            expanded: input.expandedWorkGroupIds?.has(groupId) ?? false,
            active: latestToolKeepsActivityLive,
          };
        })()
      : null;
  const activeWorkEntryIds = new Set(
    activeWorkRow !== null || latestToolFailed ? activeToolEntries.map((entry) => entry.id) : [],
  );

  return {
    activeTurnHeaderIndex,
    activeWorkRow,
    activeWorkEntryIds,
    latestToolFailed,
    latestToolKeepsActivityLive,
    hasLatestVisibleToolEntry: latestVisibleToolEntry !== undefined,
    activeWorkAnchorId: activeWorkPlacementEntryId ?? null,
  };
}

function timelineEntryTurnIdOf(entry: TimelineEntry): TurnId | null {
  if (entry.kind === "message") {
    return entry.message.role === "assistant" || entry.message.role === "reasoning"
      ? (entry.message.turnId ?? null)
      : null;
  }
  if (entry.kind === "proposed-plan") {
    return entry.proposedPlan.turnId;
  }
  return entry.kind === "work" ? (entry.entry.turnId ?? null) : null;
}
